import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Table,
    Tag,
    Space,
    Button,
    Tooltip,
    Typography,
    Modal,
    Input,
    Form
} from 'antd';
import {
    EyeOutlined,
    BellOutlined,
    QuestionCircleOutlined
} from '@ant-design/icons';
import { showSuccessNotification, showErrorNotification } from '@/shared/utils/notify';
import {ActionFlowRequestDTO, ActionFlowRequestSearch} from "@/shared/api/model";
import {formatDateTime} from "@/modules/action-flow/shared/utils/formatters";
import StatusBadge from "@/modules/action-flow/shared/components/StatusBadge";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface RequestListProps {
    requests: ActionFlowRequestDTO[];
    isLoading: boolean;
    error: Error | null;
    searchParams: ActionFlowRequestSearch;
    onParamsChange: (params: ActionFlowRequestSearch) => void;
    showResendAction?: boolean;
    hideSubmissionStep?: boolean;
}

const RequestList: React.FC<RequestListProps> = ({
                                                     requests,
                                                     isLoading,
                                                     error,
                                                     searchParams,
                                                     onParamsChange,
                                                     showResendAction = false,
                                                     hideSubmissionStep = false
                                                 }) => {
    const [isResendModalVisible, setIsResendModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<ActionFlowRequestDTO | null>(null);
    const [resendForm] = Form.useForm();
    const [resendLoading, setResendLoading] = useState(false);

    const handleResendNotification = (record: ActionFlowRequestDTO) => {
        setSelectedRequest(record);
        setIsResendModalVisible(true);
    };

    const handleResendSubmit = async () => {
        if (!selectedRequest) return;

        try {
            setResendLoading(true);
            const values = await resendForm.validateFields();

            // Make API call to resend notification
            const response = await fetch(`/api/v1/action-flows/${selectedRequest.scopeId}/resend`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    approvalStatus: selectedRequest.approvalStatus,
                    comment: values.comment
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to resend notification');
            }

            showSuccessNotification('Notification resent successfully');
            setIsResendModalVisible(false);
            resendForm.resetFields();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to resend notification';
            showErrorNotification(errorMessage);
        } finally {
            setResendLoading(false);
        }
    };

    const columns = [
        {
            title: 'Record Name',
            dataIndex: 'recordName',
            key: 'recordName',
            render: (text: string) => <strong>{text}</strong>
        },
        {
            title: 'Domain',
            dataIndex: 'domain',
            key: 'domain',
            render: (text: string) => (
                <Tag color="blue">{text}</Tag>
            )
        },
        {
            title: 'Branch',
            dataIndex: 'branchName',
            key: 'branchName',
        },
        {
            title: 'Current Step',
            dataIndex: 'nodeName',
            key: 'nodeName',
            render: (text: string, record: ActionFlowRequestDTO) => (
                <Tag color={
                    record.nodeType === 'validator' ? 'orange' :
                        record.nodeType === 'maker' ? 'green' :
                            'default'
                }>
                    {text}
                </Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'approvalStatus',
            key: 'approvalStatus',
            render: (status: string) => (
                <StatusBadge status={status} />
            )
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
        },
        {
            title: 'Scope Date',
            dataIndex: 'scopeDate',
            key: 'scopeDate',
            render: (date: string) => formatDateTime(date)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: ActionFlowRequestDTO) => (
                <Space>
                    <Tooltip title="View Details">
                        <Link to={`/requests/approval/${record.scopeId}`} state={{ hideSubmissionStep }}>
                            <Button
                                type="primary"
                                icon={<EyeOutlined />}
                            >
                                View
                            </Button>
                        </Link>
                    </Tooltip>

                    {showResendAction && (
                        <Tooltip title="Resend Notification">
                            <Button
                                onClick={() => handleResendNotification(record)}
                                icon={<BellOutlined />}
                            >
                                Resend
                            </Button>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    if (error) {
        return (
            <div className="error-message p-4 bg-red-50 border border-red-200 rounded-md">
                <Title level={5} className="text-red-500">Error loading requests</Title>
                <Text>{error.message}</Text>
            </div>
        );
    }

    return (
        <>
            <Table
                columns={columns}
                dataSource={requests}
                rowKey="scopeId"
                loading={isLoading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} items`
                }}
                onChange={(pagination, filters, sorter) => {
                    // Handle table changes if needed
                }}
                className="requests-table"
            />

            <Modal
                title="Resend Notification"
                open={isResendModalVisible}
                onCancel={() => {
                    setIsResendModalVisible(false);
                    resendForm.resetFields();
                }}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setIsResendModalVisible(false);
                        resendForm.resetFields();
                    }}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleResendSubmit}
                        loading={resendLoading}
                    >
                        Resend
                    </Button>,
                ]}
            >
                <div className="mb-4">
                    <div className="flex items-center mb-2">
                        <QuestionCircleOutlined className="mr-2 text-blue-500" />
                        <Text>Are you sure you want to resend the notification for this request?</Text>
                    </div>

                    {selectedRequest && (
                        <div className="bg-gray-50 p-3 rounded mt-2">
                            <p><strong>Record:</strong> {selectedRequest.recordName}</p>
                            <p><strong>Status:</strong> {selectedRequest.approvalStatus}</p>
                            <p><strong>Current Step:</strong> {selectedRequest.nodeName}</p>
                        </div>
                    )}
                </div>

                <Form form={resendForm} layout="vertical">
                    <Form.Item
                        name="comment"
                        label="Additional Comment (Optional)"
                        rules={[
                            {
                                max: 500,
                                message: 'Comment cannot exceed 500 characters'
                            }
                        ]}
                    >
                        <TextArea
                            rows={4}
                            placeholder="Add an optional comment to include with the notification"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default RequestList;